import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/client';
import { useAuth } from '../AdminRoutes';
import {
  useApi, useDebounced, formatDate, timeAgo, humanise,
} from '../hooks/useApi';
import {
  Card, StatusPill, ChannelTag, Pager, EmptyState, ErrorState,
  TableSkeleton, Drawer,
} from '../components/ui';

const STATUSES = ['new', 'in_progress', 'responded', 'converted', 'closed', 'spam'];
const CHANNELS = ['direct', 'organic_search', 'paid_search', 'social', 'referral', 'email', 'other'];

export default function Enquiries() {
  const { canWrite } = useAuth();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [channel, setChannel] = useState('all');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);

  const q = useDebounced(search);

  const params = useMemo(() => ({
    page, limit: 25,
    q: q || undefined,
    status: status === 'all' ? undefined : status,
    channel: channel === 'all' ? undefined : channel,
  }), [page, q, status, channel]);

  const list = useApi('/api/admin/contacts', params);
  const detail = useApi(`/api/admin/contacts/${openId}`, null, { enabled: Boolean(openId) });

  // Any filter change should return to page one, or the table can look empty.
  const setFilter = (setter) => (value) => { setter(value); setPage(1); };

  const updateStatus = async (id, next) => {
    try {
      await api.patch(`/api/admin/contacts/${id}`, { status: next });
      toast.success(`Marked as ${humanise(next).toLowerCase()}.`);
      list.reload();
      if (openId === id) detail.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const saveNotes = async (id, notes) => {
    try {
      await api.patch(`/api/admin/contacts/${id}`, { internal_notes: notes });
      toast.success('Notes saved.');
      detail.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const record = detail.data;
  const summary = list.meta?.summary;

  return (
    <div className="spa-stack">
      {summary && (
        <div className="spa-metrics">
          <div className="spa-metric">
            <div className="spa-metric__label">Matching</div>
            <div className="spa-metric__value">{summary.total}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">Awaiting reply</div>
            <div className="spa-metric__value" style={{ color: '#175cd3' }}>{summary.new_count}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">In progress</div>
            <div className="spa-metric__value" style={{ color: '#b54708' }}>{summary.in_progress_count}</div>
          </div>
          <div className="spa-metric">
            <div className="spa-metric__label">Converted</div>
            <div className="spa-metric__value" style={{ color: '#067647' }}>{summary.converted_count}</div>
          </div>
        </div>
      )}

      <div className="spa-toolbar">
        <div className="spa-search">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input
            className="spa-input"
            type="search"
            placeholder="Search name, email, phone or message"
            value={search}
            onChange={(e) => setFilter(setSearch)(e.target.value)}
            aria-label="Search enquiries"
          />
        </div>

        <select className="spa-select" value={status}
          onChange={(e) => setFilter(setStatus)(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
        </select>

        <select className="spa-select" value={channel}
          onChange={(e) => setFilter(setChannel)(e.target.value)} aria-label="Filter by channel">
          <option value="all">All channels</option>
          {CHANNELS.map((c) => <option key={c} value={c}>{humanise(c)}</option>)}
        </select>

        <button type="button" className="spa-btn spa-btn--sm" onClick={list.reload}>
          <i className="fa-solid fa-rotate-right" aria-hidden="true" /> Refresh
        </button>
      </div>

      <Card padded={false}>
        {list.error ? (
          <ErrorState message={list.error} onRetry={list.reload} />
        ) : list.isLoading && !list.data ? (
          <TableSkeleton cols={6} />
        ) : !list.data?.length ? (
          <EmptyState
            icon="fa-inbox"
            title="No enquiries match these filters"
            text="Try clearing the search or widening the status and channel filters."
          />
        ) : (
          <>
            <div className="spa-tablewrap">
              <table className="spa-table">
                <thead>
                  <tr>
                    <th>From</th>
                    <th>Message</th>
                    <th>Page</th>
                    <th>Channel</th>
                    <th>Status</th>
                    <th className="num">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((c) => (
                    <tr key={c.id} className="is-clickable" onClick={() => setOpenId(c.id)}>
                      <td>
                        <div className="spa-table__primary">{c.name}</div>
                        <div className="spa-table__sub">{c.email}</div>
                      </td>
                      <td style={{ maxWidth: 300 }}>
                        <div style={{
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap', color: '#475467',
                        }}>
                          {c.message}
                        </div>
                      </td>
                      <td><span className="spa-pill spa-pill--neutral">{c.source_page}</span></td>
                      <td><ChannelTag channel={c.channel} /></td>
                      <td><StatusPill status={c.status} /></td>
                      <td className="num" title={formatDate(c.created_at, { withTime: true })}>
                        {timeAgo(c.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager pagination={list.meta?.pagination} onPage={setPage} />
          </>
        )}
      </Card>

      {/* -------------------------------------------------------- drawer */}
      <Drawer
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        title={record?.name || 'Loading…'}
        subtitle={record ? formatDate(record.created_at, { withTime: true }) : null}
        footer={record && canWrite && (
          <>
            <a className="spa-btn spa-btn--primary"
              href={`mailto:${record.email}?subject=Re: your enquiry to Starparth Technologies`}>
              <i className="fa-solid fa-reply" aria-hidden="true" /> Reply by email
            </a>
            <a className="spa-btn" href={`tel:${record.phone}`}>
              <i className="fa-solid fa-phone" aria-hidden="true" /> Call
            </a>
            <select
              className="spa-select"
              value={record.status}
              onChange={(e) => updateStatus(record.id, e.target.value)}
              aria-label="Change status"
            >
              {STATUSES.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
            </select>
          </>
        )}
      >
        {!record ? (
          <TableSkeleton rows={5} cols={2} />
        ) : (
          <>
            <div className="spa-section-label">Contact</div>
            <dl className="spa-dl">
              <dt>Email</dt><dd><a href={`mailto:${record.email}`}>{record.email}</a></dd>
              <dt>Phone</dt><dd><a href={`tel:${record.phone}`}>{record.phone}</a></dd>
              {record.website && (
                <><dt>Website</dt><dd>
                  <a href={record.website} target="_blank" rel="noreferrer">{record.website}</a>
                </dd></>
              )}
              <dt>Interest</dt><dd>{record.service_interest || '—'}</dd>
              <dt>Status</dt><dd><StatusPill status={record.status} /></dd>
            </dl>

            <div className="spa-section-label">Message</div>
            <div className="spa-quote">{record.message}</div>

            <div className="spa-section-label">Attribution</div>
            <dl className="spa-dl">
              <dt>Channel</dt><dd><ChannelTag channel={record.channel} /></dd>
              <dt>Page</dt><dd>{record.source_page}</dd>
              {record.utm_campaign && (<><dt>Campaign</dt><dd>{record.utm_campaign}</dd></>)}
              {record.utm_source && (<><dt>Source</dt><dd>{record.utm_source} / {record.utm_medium || '—'}</dd></>)}
              {record.referrer && (
                <><dt>Referrer</dt><dd style={{ fontSize: '0.8rem', color: '#667085' }}>{record.referrer}</dd></>
              )}
              <dt>Device</dt>
              <dd>{[record.device_type, record.browser, record.os].filter(Boolean).join(' · ') || '—'}</dd>
            </dl>

            {record.journey?.length > 0 && (
              <>
                <div className="spa-section-label">
                  What they looked at ({record.journey.length} steps)
                </div>
                <ul className="spa-trail">
                  {record.journey.slice(0, 15).map((step, i) => (
                    <li key={i}>
                      <time>{formatDate(step.created_at, { withTime: true })}</time>
                      <span>
                        {step.kind === 'visit' ? 'Viewed' : 'Did'} <strong>{step.page}</strong>
                        {step.label ? ` — ${step.label}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {canWrite && (
              <>
                <div className="spa-section-label">Internal notes</div>
                <NotesEditor
                  key={record.id}
                  initial={record.internal_notes || ''}
                  onSave={(text) => saveNotes(record.id, text)}
                />
              </>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}

/** Kept local: only this drawer needs a save-on-demand text area. */
function NotesEditor({ initial, onSave }) {
  const [text, setText] = useState(initial);
  const dirty = text !== initial;

  return (
    <div>
      <textarea
        className="spa-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Context for whoever picks this up next — what was promised, what is outstanding."
      />
      <button
        type="button"
        className="spa-btn spa-btn--sm spa-btn--primary"
        style={{ marginTop: 8 }}
        disabled={!dirty}
        onClick={() => onSave(text)}
      >
        {dirty ? 'Save notes' : 'Saved'}
      </button>
    </div>
  );
}
